import {
  CardPriceCurrenciesEnum,
  CardPriceMarketEnum,
  EmbeddedCardPrice,
} from "modules/cards/model/card-price";
import { SortOrderEnum } from "modules/collections/details/model/collection";
import { LGS } from "modules/lgs/model/lgs";
import { ShortCardSet, type GameId } from "../../common/model/models";
import {
  CardLanguageEnum,
  type FinishEnum,
  NONFOIL_FINISH,
  CardConditionEnum,
} from "modules/cards/model/card-enums";
export { CardLanguageEnum, type FinishEnum, NONFOIL_FINISH, CardConditionEnum };

export interface ForeignData {
  faceName: string;
  flavorText: string;
  language: CardLanguageEnum;
  multiverseId: number;
  name: string;
  text: string;
  type: string;
}

export interface LeadershipSkills {
  brawl: boolean;
  commander: boolean;
  oathbreaker: boolean;
}

export enum LegalityStatus {
  Legal = "Legal",
  Banned = "Banned",
  Restricted = "Restricted",
}

export const defaultLegalities = [
  LegalityStatus.Legal,
  LegalityStatus.Banned,
  LegalityStatus.Restricted,
];

export type Legalities = Record<string, LegalityStatus>;

export interface Ruling {
  date: string;
  text: string;
}

export enum ManaColorEnum {
  Black = "B",
  Red = "R",
  Green = "G",
  Blue = "U",
  White = "W",
  Colorless = "C",
  Devoid = "D",
}

export enum BorderColorEnum {
  Black = "black",
  Borderless = "borderless",
  Gold = "gold",
  Silver = "silver",
  White = "white",
}

export enum AvailabilityEnum {
  Paper = "paper",
  MTGO = "mtgo",
}

export enum FrameEffectsEnum {
  ColorShifted = "colorshifted",
  Companion = "companion",
  CompassLandDfc = "compasslanddfc",
  Devoid = "devoid",
  Draft = "draft",
  Etched = "etched",
  ExtendedArt = "extendedart",
  FullArt = "fullart",
  Inverted = "inverted",
  Legendary = "legendary",
  Lesson = "lesson",
  Miracle = "miracle",
  MoonEldraziDfc = "mooneldrazidfc",
  NyxTouched = "nyxtouched",
  OriginPwDfc = "originpwdfc",
  Showcase = "showcase",
  Snow = "snow",
  SunMoonDfc = "sunmoondfc",
  Tombstone = "tombstone",
  WaxingAndWaningMoonDfc = "waxingandwaningmoondfc",
}

export enum CardLayoutEnum {
  Adventure = "adventure",
  Aftermath = "aftermath",
  ArtSeries = "art_series",
  Augment = "augment",
  Class = "class",
  DoubleFacedToken = "double_faced_token",
  Emblem = "emblem",
  Flip = "flip",
  Host = "host",
  Leveler = "leveler",
  Meld = "meld",
  ModalDfc = "modal_dfc",
  Normal = "normal",
  Planar = "planar",
  Prepare = "prepare",
  ReversibleCard = "reversible_card",
  Saga = "saga",
  Scheme = "scheme",
  Split = "split",
  Token = "token",
  Transform = "transform",
  Vanguard = "vanguard",
}

export interface CommonPagedCard {
  id: string;
  name: string;
  number: string;
  costPrinted: string;
  costValue: number;
  finishes: FinishEnum[];
  type: string;
  types: string[];
  price?: EmbeddedCardPrice;
  setCode: string;
  originalSetCode?: string;
  rarity: string;
}

export interface CommonMiniCard {
  id: string;
  g: string;
  s: string;
  nb: string;
  n: string;
  sl: string;
  pk?: string;
}
export interface BaseCard {
  id: string;
  game: GameId;
  name: string;
  slug: string;
  costPrinted: string;
  costValue: number;
  artist: string;
  finishes: FinishEnum[];
  number: string;
  flavorName?: string;
  flavorText?: string;
  type: string;
  types: string[];
  setCode: string;
  originalSetCode?: string;
  rarity: string;
  text: string;
  legalities: Legalities;
  languages?: CardLanguageEnum[];
  price?: EmbeddedCardPrice;
  set: ShortCardSet;
  printing?: CardPrinting;
}

export interface CardPrintingMarketSku {
  provider: CardPriceMarketEnum;
  productId: string;
  priceVariant?: string;
  finish?: FinishEnum;
  language?: CardLanguageEnum;
}

export interface CardPrinting {
  printedNumber: string;
  key?: string;
  variant?: string;
  treatment?: string;
  distribution?: string;
  isPromo?: boolean;
  marketSkus?: CardPrintingMarketSku[];
}

export interface MagicCard extends BaseCard {
  features: MagicCardFeatures;
}

export interface PokemonCard extends BaseCard {
  features: PokemonCardFeatures;
}

export interface RiftboundCard extends BaseCard {
  features: RiftboundCardFeatures;
}

export interface OnePieceCard extends BaseCard {
  features: OnePieceCardFeatures;
}

export interface StarWarsUnlimitedCard extends BaseCard {
  features: StarWarsUnlimitedCardFeatures;
}

export interface LorcanaCard extends BaseCard {
  features: LorcanaCardFeatures;
}

export interface FusionWorldCard extends BaseCard {
  features: FusionWorldCardFeatures;
}

export interface FleshAndBloodCard extends BaseCard {
  features: FleshAndBloodCardFeatures;
}

export interface GenericGameCard extends BaseCard {
  features: {
    layout?: CardLayoutEnum;
    otherFaces?: CardFace[];
    [key: string]: unknown;
  };
}

export type GameCard =
  | MagicCard
  | PokemonCard
  | RiftboundCard
  | OnePieceCard
  | StarWarsUnlimitedCard
  | LorcanaCard
  | FusionWorldCard
  | FleshAndBloodCard
  | GenericGameCard;

// CardFace is the slim representation of a DFC/MDFC back face. Mirrors
// card.CardFace on the server (api/domain/card/magic.go). Carries only
// the fields the inline face renderer reads — image rendering still uses
// `${cdn}/card/${parentId}/back` against the parent card id.
export interface CardFace {
  id: string;
  oracleId: string;
  name: string;
  slug: string;
  number: string;
  setCode: string;
  costPrinted?: string;
  costValue?: number;
  type: string;
  types: string[];
  text?: string;
  flavorText?: string;
  finishes: FinishEnum[];
  layout?: CardLayoutEnum;
}

// Mirrors the Go MagicFeatures struct's serialized surface
// (api/domain/card/magic.go). Anything tagged json:"-" on the server is
// excluded here. Add a field back only when the API actually returns it.
//
// foreignData and rulings are gated server-side behind ?expand=foreignData
// and ?expand=rulings respectively — only the card-detail page opts in.
export interface MagicCardFeatures {
  colorIdentity: ManaColorEnum[];
  colors: ManaColorEnum[];
  faceManaValue?: number;
  faceName?: string;
  foreignData?: ForeignData[];
  isReprint?: boolean;
  keywords?: string[];
  language: CardLanguageEnum;
  layout: CardLayoutEnum;
  leadershipSkills?: LeadershipSkills;
  printings: string[];
  rulings?: Ruling[];
  otherFaces?: CardFace[];
}

export interface RelatedCards {
  reverseRelated?: string[];
  spellbook?: string[];
}

export interface PokemonCardFeatures {
  category: string;
  stage: string;
  hp: number;
  types: string[];
  evolveFrom?: string;
  evolvesTo?: string[];
  attacks?: PokemonAttack[];
  weaknesses?: PokemonWeakness[];
  retreat: number;
  regulationMark?: string;
  dexId?: number[];
  description?: string;
  rules?: string[];
  suffix?: string;
  level?: string;
}

export interface PokemonAttack {
  name: string;
  cost: string[];
  damage?: string;
  effect?: string;
}

export interface PokemonWeakness {
  type: string;
  value: string;
}

export interface RiftboundCardFeatures {
  tags: string[];
  domains: string[];
  costPower: number;
  might: number;
}

// BannedPairPartner is the OTHER card in a Bandai banned-pair constraint.
// Stored on each affected One Piece card under
// `features.banned_pairs[formatSlug]`. Code is the human-readable Bandai
// identifier (e.g. "OP11-067"), always present. OracleID is our internal
// stable card identity that collapses variant printings — populated when
// the partner is in our DB, omitted otherwise. Consumers should prefer
// OracleID for navigation and fall back to Code for display.
export interface BannedPairPartner {
  code: string;
  oracleId?: string;
}

export interface OnePieceCardFeatures {
  opAttributes: string[];
  opPower: string;
  opCounter: string;
  opColors: string[];
  opSubTypes: string[];
  opLife: string;
  opKeywords: string[];
  opEffectTags: string[];
  // bannedPairs is written by the legality sync; absent on cards that
  // are not part of any pair-constraint in any tracked format.
  bannedPairs?: Record<string, BannedPairPartner[]>;
}

export interface StarWarsUnlimitedCardFeatures {
  swuAspects: string[];
  swuTraits: string[];
  swuArena: string;
  swuPower: number;
  swuHP: number;
  swuEpicAction: string;
}

export interface LorcanaCardFeatures {
  lorcanaInks: string[];
  lorcanaClassifications: string[];
  lorcanaKeywords: string[];
  lorcanaInkable: boolean;
  lorcanaStrength: number;
  lorcanaWillpower: number;
  lorcanaLore: number;
  lorcanaMoveCost: number;
}

export interface FusionWorldCardFeatures {
  fusionWorldColors: string[];
  fusionWorldTraits: string[];
  fusionWorldPower: string;
  fusionWorldComboPower: string;
}

export interface FleshAndBloodCardFeatures {
  fabColors: string[];
  fabTraits: string[];
  fabKeywords: string[];
  fabPitch: number;
  fabPower: string;
  fabDefense: string;
  fabHealth: string;
  fabIntelligence: string;
  fabArcane: string;
  fabBackSourceId?: string;
  fabBackName?: string;
  fabBackNumber?: string;
  fabBackColor?: string;
  fabBackPitch?: number;
  fabBackCost?: string;
  fabBackPower?: string;
  fabBackDefense?: string;
  fabBackHealth?: string;
  fabBackIntelligence?: string;
  fabBackArcane?: string;
  fabBackType?: string;
  fabBackTypes?: string[];
  fabBackTraits?: string[];
  fabBackKeywords?: string[];
  fabBackText?: string;
  fabBackImageUrl?: string;
}

export interface CardFilterOptions {
  string: string;
  foil: boolean;
  playable: boolean;
  setCode: string;
  format: string;
  rarity: string;
  types: string[];
  game?: GameId;
}

export interface AutocompleteGameCardsRequest extends CardFilterOptions {
  pageSize: number;
  page: number;
}

export interface SearchMagicCardsRequest {
  fields: string[];
  pageSize: number;
  setCode: string;
  string: string;
}

export interface GameCardPrintingsQuery {
  perPage: number;
  page: number;
  sort?: string;
  order?: string;
  includeSelf?: boolean;
}

export enum PrintingsSortEnum {
  ReleaseDate = "releaseDate",
  Price = "price",
  PriceFoil = "priceFoil",
}

export interface PrintingsPagedResponse {
  cards: GameCard[];
  hasMore: boolean;
  total: number;
}

export interface CardPrintingOption {
  id: string;
  game: GameId;
  name: string;
  number: string;
  setCode: string;
  setName: string;
  releaseDate: string;
  finishes: FinishEnum[];
}

export interface PrintingOptionsResponse {
  options: CardPrintingOption[];
}

export interface AutocompleteGameCard {
  game: GameId;
  isPromo: boolean;
  name: string;
  slug: string;
  number: string;
  price: {
    default: number;
    nonfoil?: number;
    foil: number;
    metal?: number;
    metalDeluxe?: number;
    etched?: number;
    currency: CardPriceCurrenciesEnum;
  };
  setCode: string;
  id: string;
  costPrinted: string;
  language: string;
  finishes: FinishEnum[];
  keyruneCode: string;
  printingKey?: string;
}

export interface AutocompleteGameCardsResponse {
  items: AutocompleteGameCard[];
  hasMore: boolean;
}

export interface CardChartRequest {
  cardId: string;
  currency?: CardPriceCurrenciesEnum;
  market?: CardPriceMarketEnum;
  finish: string;
}

export interface CardChart {
  cardId: string;
  currency: CardPriceCurrenciesEnum;
  market: CardPriceMarketEnum;
  timestamps: string[];
  values: number[];
  lowValues?: number[];
  averages: CardChartAverages;
}

// Wire response from GET /cards/{id}/chart. `chart` is null when the
// requested finish has no chartable data and no fallback applied.
// `availableFinishes` lists every finish that DOES have data, including
// the requested one when applicable, so the UI can render finish toggles
// without a second round trip.
export interface CardChartResponse {
  finish: FinishEnum;
  chart: CardChart | null;
  availableFinishes: FinishEnum[];
}

export interface CardChartAverages {
  ma: CardPriceAveragePeriod;
  // ema: CardPriceAveragePeriod;
}

export interface CardPriceAveragePeriod {
  "7d": number[];
  "14d": number[];
  "30d": number[];
}

export enum CardSortOptionsEnum {
  Name = "name",
  Type = "types",
  Price = "price",
  Rarity = "rarity",
  ManaValue = "manaValue",
  Number = "number",
  PriceDeltaAbs = "priceDeltaAbs",
  PriceDeltaPercent = "priceDeltaPercent",
}

export enum PricePeriodEnum {
  ThreeDays = "3d",
  SevenDays = "7d",
  FourteenDays = "14d",
  ThirtyDays = "30d",
}

export interface CardsPagedRequest {
  page: number;
  perPage: number;
  sort: CardSortOptionsEnum;
  order: SortOrderEnum;
  pricePeriod: PricePeriodEnum;
  filter: CardFilterOptions;
}

export interface CardsPagedResponse {
  cards: GameCard[];
  hasMore: boolean;
}
export interface MiniCardsPagedResponse {
  cards: CommonMiniCard[];
  hasMore: boolean;
}

export interface CardLGSAvailability {
  available: LGS[];
  wanted: LGS[];
}

export interface CardCityAvailability {
  setCode: string;
  collectorNumber: string;
  cities: CityInfo[];
}

export interface CityInfo {
  latitude: number;
  longitude: number;
  cityName?: string;
  country?: string;
  availableCount: number;
  wantedCount: number;
  storeCount: number;
  storeIds: number[];
}
